<!-- eslint-disable max-len -->
<template>
  <div
    class="row form-page-detail-body-container">
    <div class="w-100 d-flex justify-content-end mb-3">
      <button
        v-if="showBtnUpdateTable"
        class="form-page-detail-button"
        @click="openModalUpdateTable($v.form)">
        Save
      </button>
    </div>

    <div class="form-page-detail-wrap">
      <div
        v-if="form.is_offline_mode"
        class="local-storage">
        <img
          src="@/assets/logo/offline-mode.png"
          height="16">
        Local Storage Support
      </div>
      <b-form-group :class="{'error-input' : $v.form.name.$error}">
        <label>Table Name</label>
        <j-input
          v-model="$v.form.name.$model"
          :disabled="disableAction"
          placeholder="Only lowercase characters and underscore '_' are allowed, ex: name_form"
        />
      </b-form-group>
      <b-form-group>
        <label>Table Id</label>
        <j-input
          v-model="$v.form.id.$model"
          :disabled="true"
        />
      </b-form-group>

      <div class="form-page-detail-config">
        <div class="config-item">
          <div class="config-item-toggle">
            <div class="config-item-toggle-wrap">
              <h4 class="config-item-title">Sharing Device Info</h4>
              <p class="config-item-desc">The mobile app will ask for device info when performing any action on this table.</p>
            </div>
            <j-switch
              :class="{'disabled': disableAction || isModalUpdateTableOpen}"
              v-model="form.is_share_device_info"
              :disabled="disableAction || isModalUpdateTableOpen"
            />
          </div>
        </div>
        <hr class="mt-2 mb-4">
        <div class="config-item mt-2">
          <div class="config-item-toggle">
            <div class="config-item-toggle-wrap">
              <h4 class="config-item-title">Disable Log</h4>
              <p class="config-item-desc">Any CREATE, UPDATE, DELETE, TRUNCATE actions on data will not be included in the audit trail log.</p>
            </div>
            <j-switch
              :class="{'disabled': disableActionAuditLog || isModalUpdateTableOpen}"
              v-model="form.is_disable_audit_log"
              :disabled="disableActionAuditLog || isModalUpdateTableOpen"
            />
          </div>
        </div>
        <hr class="mt-2 mb-4">
        <div class="config-item mt-2">
          <div class="config-item-toggle">
            <div class="config-item-toggle-wrap">
              <h4 class="config-item-title">Data Load Maximum Limit</h4>
              <p class="config-item-desc">The maximum number of data retrieves allowed is 10,000 records.</p>
            </div>
            <j-switch v-model="form.is_limit_maximum_data"/>
          </div>
        </div>
        <hr class="mt-2 mb-4">
        <div class="config-item mt-2">
          <div class="config-item-toggle">
            <div class="config-item-toggle-wrap">
              <h4 class="config-item-title">Rate Limits</h4>
              <p class="config-item-desc">Rate limit is a restriction on the number of requests within a specific time period to prevent resource abuse and maintain system performance.</p>
            </div>
            <j-switch v-model="isRateLimitActive" />
          </div>
          <div
            v-show="isRateLimitActive"
            class="config-item-input">
            <p>Maximum Requests</p>
            <div class="line" />
            <input
              :class="{'error-input': $v.form.rate_limit.$error}"
              v-model="limitPerSecond"
              type="number"
            >
            <span>per seconds</span>
          </div>
        </div>
        <hr
          v-if="switcher"
          class="mt-2 mb-4">
        <div
          v-if="switcher"
          class="config-item mt-2">
          <div class="config-item-toggle">
            <div class="config-item-toggle-wrap">
              <h4 class="config-item-title">Specific Running Service</h4>
            </div>
          </div>
          <b-form>
            <label> Service </label>
            <j-single-select
              v-model="nocodeServiceTarget"
              :options="serviceOptions"
              :error-required="false"
              placeholder="Select Service"
              label="name"
            />
            <span>For running process on specific service</span>
          </b-form>
        </div>
      </div>
    </div>

    <j-modal-update-table
      v-if="isShowModalUpdateTable"
      id="modalUpdateTableId"
      :table-name="form.name"
      @submit="submitForm"
      @close="closeModalUpdateTable"
    />
  </div>
</template>

<script>
/* eslint-disable import/no-extraneous-dependencies, no-nested-ternary, max-len, no-console, no-unused-vars */
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mapState, mapActions, mapMutations } from 'vuex';
import jwtDecode from 'jwt-decode';
import jModalUpdateTable from '@/views/data-manager/jModalUpdateTable';
import jMixModalUpdateTable from '@/mixins/jMixModalUpdateTable';
import jSwitch from '@/components/inputs/jSwitch';

const mustGreaterThanZero = (value, parentVm) => {
  if (parentVm.active) return value > 0;
  return true;
};

export default {
  // eslint-disable-next-line
  name: 'jFormGeneral',

  components: {
    jModalUpdateTable,
    jSwitch,
  },

  mixins: [jMixModalUpdateTable],

  props: {
    switcher: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        name: '',
        id: '',
        type: null,
        is_public: false,
        is_share_device_info: false,
        is_offline_mode: false,
        is_disable_audit_log: false,
        is_limit_maximum_data: false,
        rate_limit: {
          active: false,
          limit_per_second: 0,
        },
      },
      isRateLimitActive: false,
      limitPerSecond: 0,
      findRecord: true,
      session: jwtDecode(JSON.parse(localStorage.getItem('Token')).value),
      nocodeServiceOrigin: 'nocode-service-0',
      nocodeServiceTarget: 'nocode-service-0',
      serviceOptions: [],
    };
  },
  computed: {
    ...mapState('jStoreForm', ['form_data', 'privilege_by_me']),

    isAdmin() {
      const role = localStorage.getItem('userRole');
      return Number(role) === 1;
    },

    disableAction() {
      return this.findRecord || this.form_data.type === 'thirdparty' || (this.privilege_by_me.includes('view_only') && !this.isAdmin);
    },

    disableActionAuditLog() {
      return this.form_data.type === 'thirdparty' || (this.privilege_by_me.includes('view_only') && !this.isAdmin);
    },

    showBtnUpdateTable() {
      return this.isAdmin || (this.privilege_by_me.includes('alter_table') && !this.privilege_by_me.includes('view_only'));
    },
  },
  watch: {
    // eslint-disable-next-line
    'form.name': function (val){
      const regexSymbol = /[^\w\s]/g;
      setTimeout(() => {
        this.form.name =
          val.toLowerCase()
            .replace(regexSymbol, '')
            .replace(' ', '_');
      }, 1);

      if (this.form_data.name) {
        if (this.form_data.name !== val) {
          this.setChangesValueView(true);
        }
      } else {
        this.setChangesValueView(true);
      }
    },

    // eslint-disable-next-line
    'form.is_public': function (val){
      if (this.form_data.is_public !== val) {
        this.setChangesValueView(true);
      } else {
        this.setChangesValueView(false);
      }
    },

    // eslint-disable-next-line
    'form.is_share_device_info': function (val){
      if (this.form_data.is_share_device_info !== val) {
        this.setChangesValueView(true);
      } else {
        this.setChangesValueView(false);
      }
    },

    // eslint-disable-next-line
    'form.is_disable_audit_log': function (val){
      if (this.form_data.is_disable_audit_log !== val) {
        this.setChangesValueView(true);
      } else {
        this.setChangesValueView(false);
      }
    },

    // eslint-disable-next-line
    'form.is_limit_maximum_data': function (val){
      if (this.form_data.is_limit_maximum_data !== val) {
        this.setChangesValueView(true);
      } else {
        this.setChangesValueView(false);
      }
    },

    // eslint-disable-next-line
    'form.rate_limit.active': function (val){
      if (this.form_data.rate_limit.active !== val) {
        this.setChangesValueView(true);
      } else {
        this.setChangesValueView(false);
      }
    },

    // eslint-disable-next-line
    'form.rate_limit.limit_per_second': function (val){
      if (this.form_data.rate_limit.limit_per_second !== val) {
        this.setChangesValueView(true);
      } else {
        this.setChangesValueView(false);
      }
    },

    isRateLimitActive(val) {
      this.form.rate_limit.active = val;

      if (!val) this.limitPerSecond = 0;
    },

    limitPerSecond(val) {
      this.form.rate_limit.limit_per_second = val !== '' ? Number(val) : val;
    },

    form_data(val) {
      this.form.name = val.name;
      this.form.id = val.id;
      this.form.type = val.type;
      this.form.is_public = val.is_public ? val.is_public : false;
      this.form.is_share_device_info = val.is_share_device_info ? val.is_share_device_info : false;
      this.form.is_offline_mode = val.is_offline_mode || false;
      this.form.is_disable_audit_log = val.is_disable_audit_log || false;
      this.form.is_limit_maximum_data = val.is_limit_maximum_data || false;

      this.isRateLimitActive = val.rate_limit.active || false;
      this.limitPerSecond = val.rate_limit.limit_per_second || 0;
    },
  },
  created() {
    this.setChangesValueView(false);
    if (this.form_data) {
      const reqTable = {
        tablegroup_id: this.$route.params.id,
        search: this.form_data.id,
      };
      this.findListTableByGroup(reqTable).then((res) => {
        if (res.data && res.data.length > 0) {
          const dataForm = res.data.find(f => f.id === this.form_data.id);
          this.actionSetFormData(dataForm);
        }
      });

      const reqData = {
        form_data_id: this.form_data.id,
      };
      this.getCountDataRecord(reqData).then((response) => {
        this.findRecord = response.data.count > 0;
      });

      if (this.switcher) {
        this.getListServiceSwitcher().then((res) => {
          if (!res.error) this.serviceOptions = res.data.map(val => ({ id: val, name: val }));
        });

        this.actionServiceSwitcher({
          action: 'getFormDataSwitcher',
          param: { form_data_id: this.form_data.id },
        }).then((res) => {
          if (!res.error) {
            this.nocodeServiceOrigin = res.data.nocode_service_target;
            this.nocodeServiceTarget = res.data.nocode_service_target;
          }
        });
      }
    }
  },
  methods: {
    ...mapActions('jStoreForm', [
      'updateForm',
      'getCountDataRecord',
      'findForm',
      'actionSetFormData',
      'updateTable',
      'findListTableByGroup',
      'getListServiceSwitcher',
      'actionServiceSwitcher',
    ]),
    ...mapMutations('jStoreFormUi', ['setChangesValueView']),
    ...mapActions('jStoreNotificationScreen', ['toggleProblem', 'showLoading', 'hideLoading']),

    submitForm() {
      const session = this.session;
      const param = this._.cloneDeep(this.form_data);
      if (!this.findRecord) {
        param.name = this.form.name;
      }
      param.is_public = this.form.is_public;
      param.is_share_device_info = this.form.is_share_device_info;
      param.is_disable_audit_log = this.form.is_disable_audit_log;
      param.is_limit_maximum_data = this.form.is_limit_maximum_data;
      param.rate_limit = this.form.rate_limit;
      delete param.columns;

      // this.updateForm({ param, session }).then((response) => {
      //   this.setChangesValueView(false);
      //   if (response.error) {
      //     // eslint-disable-next-line
      //     console.log('gagal');
      //     this.$store.commit('jStoreNotificationScreen/toggleProblem', response.message);
      //   }
      // });

      this.updateTable({ param }).then((res) => {
        if (res.error) {
          this.toggleProblem(res.message);
        } else if (!res.error && res.error_session) {
          this.toggleInfo(res.message);
        } else {
          this.setChangesValueView(false);

          if (this.switcher && this.nocodeServiceOrigin !== this.nocodeServiceTarget) {
            this.actionServiceSwitcher({
              action: 'setFormDataSwitcher',
              param: {
                id: param.id,
                tablegroup_id: this.$route.params.id,
                nocode_service_target: this.nocodeServiceTarget,
              },
            });
          }
        }
      });

      this.closeModalUpdateTable();
    },
  },
  validations() {
    return {
      form: {
        name: { required },
        id: { },
        type: { },
        is_public: { },
        is_share_device_info: { },
        is_offline_mode: { },
        is_disable_audit_log: { },
        rate_limit: {
          limit_per_second: {
            required: requiredIf(() => this.isRateLimitActive),
            mustGreaterThanZero,
          },
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped>
  .form-page-detail-body-container {
    width: 727px;
    overflow: auto;
    margin: 0px auto;
    height: 100%;
    padding: 30px 15px;
  }

  .move {
    cursor: move;
  }

  .local-storage {
    font-family: 'Poppins-Medium';
    font-size: 11px;
    color: #007AFF;
    margin-bottom: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/views/data-manager/jFormGeneral.vue